import React from 'react';
import SideNavDivider from './SideNavDivider';
import { colorVariant } from './colorVariant';
import cn from '@/utils/cn';
import { CrossIcon } from '@/components/Icons';

export interface SideNavSubMenuItem {
  id?: string;
  label: string;
  description?: string;
  route?: string;
  onClick?: () => void;
  dividerAfter?: boolean;
  isActive?: boolean;
  onRemove?: () => void;
}

interface SideNavSubMenuProps {
  subItems: SideNavSubMenuItem[];
  loading?: boolean;
}

const CONTAINER = 'flex flex-col pl-4';

const ROW = 'flex items-stretch gap-4';

const INDICATOR = 'w-[1px] flex-shrink-0 rounded-full';

const SUB_ITEM = cn(
  colorVariant({ text: 'white' }),
  // `transition` (not transition-colors) - needs to animate both nav-item-hover's background
  // fade AND the opacity swap below, not just colours.
  'nav-item-hover flex-1 min-w-0 text-left py-2.5 pl-2 pr-3 text-xs font-medium transition rounded-lg'
);

const DESCRIPTION = cn(colorVariant({ text: 'navy-4' }), 'text-2xs font-normal leading-tight');

const REMOVE_BUTTON =
  'flex-shrink-0 flex items-center justify-center text-white opacity-70 hover:opacity-100 transition-opacity';

const SKELETON_ITEM = 'nav-skeleton animate-pulse rounded-lg mx-2 mb-1.5';

const SideNavSubMenu = ({ subItems, loading }: SideNavSubMenuProps) => {
  if (loading) {
    return (
      <div className={CONTAINER}>
        {[32, 24, 28].map((w, i) => (
          <div key={i} className={SKELETON_ITEM} style={{ height: 32, width: `${w * 4}px` }} />
        ))}
      </div>
    );
  }

  return (
    <div className={CONTAINER}>
      {subItems.map((item) => (
        <React.Fragment key={item.id ?? item.route ?? item.label}>
          <div className={ROW}>
            <div className={cn(INDICATOR, item.isActive ? 'bg-emerge-teal' : 'bg-white/20')} />
            <button
              type="button"
              // Per Figma: white text throughout, no teal - the active sub-item is full opacity,
              // every other one sits at 50% (brightening on hover, same as the design's prototype).
              // The left indicator above still marks the active item in teal - only the text
              // itself dropped teal, per the requested change.
              className={cn(
                SUB_ITEM,
                item.isActive ? 'opacity-100' : 'opacity-50 hover:opacity-100'
              )}
              onClick={item.onClick}>
              <div className="flex items-center gap-2">
                <span className="truncate">{item.label}</span>
              </div>
              {item.description && <div className={DESCRIPTION}>{item.description}</div>}
            </button>
            {item.isActive && item.onRemove && (
              <button
                type="button"
                aria-label="Remove from favorites"
                title="Remove from favorites"
                onClick={item.onRemove}
                className={REMOVE_BUTTON}>
                <CrossIcon size="xs" />
              </button>
            )}
          </div>
          {item.dividerAfter && <SideNavDivider color="navy-3" />}
        </React.Fragment>
      ))}
    </div>
  );
};

export default SideNavSubMenu;
